import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  API_BASE_URL,
  ApiClient,
  BatchAddItemDto,
  BatchRemoveItemDto,
  BatchUpdateCustomerSatisfactionVisitsRequest,
  BatchUpdateCustomerSatisfactionVisitsResponse,
  BatchUpdateItemDto,
  BatchUpdateProjectInstallDatesRequest,
  BatchUpdateProjectInstallDatesResponse,
  CalendarCrewScheduleResult,
  CreateProjectInstallDateRequest,
  CreateTravelAssignmentRequest,
  CrewBonusPercentageResponse,
  CrewMemberAllocationDto,
  CrewMemberResponse,
  DeleteProjectInstallDateResponse,
  ForemanDetailInfo,
  GetForemanAvailabilityDatesResponse,
  MonthlyCapacityResult,
  ProjectInstallDateResponse,
  TravelAssignmentResponse,
  UpdateProjectInstallDateRequest,
  UpdateTravelAssignmentCrewRequest,
  UserInfo,
} from '../sdk/sdk';
import { CalendarRole, DEFAULT_CALENDAR_ROLES } from '../models/calendar-role';
import { formatLocalIsoDate, parseLocalIsoDate } from '../utils/iso-date';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface CrewMemberAllocation {
  id?: string;
  userId: string;
  allocationPercentage: number | null;
}

export interface BatchUpdateInstallDateItem {
  id: string;
  date: string;
  foremanUserId: string;
  startTime?: string;
  endTime?: string;
  crewMembers?: CrewMemberAllocation[];
}

export interface BatchAddInstallDateItem {
  date: string;
  foremanUserId: string;
  startTime?: string;
  endTime?: string;
  crewMembers?: CrewMemberAllocation[];
}

export interface BatchRemoveInstallDateItem {
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  private readonly sdk: ApiClient = inject(ApiClient);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly baseUrl: string = inject(API_BASE_URL, { optional: true }) ?? '';
  private readonly dateRange$ = new BehaviorSubject<DateRange | null>(null);
  private readonly selectedForemenIds$ = new BehaviorSubject<string[]>([]);
  private readonly selectedRoles$ = new BehaviorSubject<CalendarRole[]>([...DEFAULT_CALENDAR_ROLES]);
  private readonly selectedAbilities$ = new BehaviorSubject<string[]>([]);
  private readonly availableAbilities$ = new BehaviorSubject<string[]>([]);

  getCrewSchedules(
    branchId: string,
    startDate: Date,
    endDate: Date,
  ): Observable<CalendarCrewScheduleResult> {
    return this.sdk.getCrewSchedules(branchId, startDate, endDate);
  }

  getForemen(branchId: string, startDate: Date, endDate: Date): Observable<UserInfo[]> {
    return this.sdk.getForemen(branchId, startDate, endDate);
  }

  getActiveUsers(branchId: string): Observable<UserInfo[]> {
    return this.sdk.getActiveUsers(branchId);
  }

  getMonthlyCapacity(
    branchId: string,
    startDate: Date,
    endDate: Date,
  ): Observable<MonthlyCapacityResult> {
    return this.sdk.getMonthlyCapacity(branchId, startDate, endDate);
  }

  addInstallDate(
    projectId: string,
    foremanUserId: string,
    date: Date,
    branchId?: string,
    startTime?: string,
    endTime?: string,
  ): Observable<ProjectInstallDateResponse> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const request = new CreateProjectInstallDateRequest({
      date,
      branchDate: today,
      foremanUserId,
      branchId: branchId || undefined,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
    });
    return this.sdk.createProjectInstallDate(projectId, request);
  }

  updateInstallDate(
    projectId: string,
    installDateId: string,
    date: Date,
    foremanUserId: string,
    startTime?: string,
    endTime?: string,
  ): Observable<ProjectInstallDateResponse> {
    const todayForUpdate = new Date();
    todayForUpdate.setHours(0, 0, 0, 0);
    const request = new UpdateProjectInstallDateRequest({
      date,
      foremanUserId,
      branchDate: todayForUpdate,
      startTime: startTime || undefined,
      endTime: endTime || undefined,
    });
    return this.sdk.updateProjectInstallDate(projectId, installDateId, request);
  }

  removeInstallDate(
    projectId: string,
    installDateId: string,
  ): Observable<DeleteProjectInstallDateResponse> {
    return this.sdk.deleteProjectInstallDate(projectId, installDateId);
  }

  setDateRange(startDate: Date, endDate: Date): void {
    this.dateRange$.next({ startDate, endDate });
  }

  getDateRange(): Observable<DateRange | null> {
    return this.dateRange$.asObservable();
  }

  getCurrentDateRange(): DateRange | null {
    return this.dateRange$.getValue();
  }

  getSelectedForemenIds(): Observable<string[]> {
    return this.selectedForemenIds$.asObservable();
  }

  getCurrentSelectedForemenIds(): string[] {
    return this.selectedForemenIds$.getValue();
  }

  setSelectedForemenIds(ids: string[]): void {
    this.selectedForemenIds$.next(ids);
  }

  clearSelectedForemen(): void {
    this.selectedForemenIds$.next([]);
  }

  getSelectedRoles(): Observable<CalendarRole[]> {
    return this.selectedRoles$.asObservable();
  }

  getCurrentSelectedRoles(): CalendarRole[] {
    return this.selectedRoles$.getValue();
  }

  setSelectedRoles(roles: CalendarRole[]): void {
    this.selectedRoles$.next(roles);
  }

  clearSelectedRoles(): void {
    this.selectedRoles$.next([]);
  }

  resetSelectedRolesToDefault(): void {
    this.selectedRoles$.next([...DEFAULT_CALENDAR_ROLES]);
  }

  getSelectedAbilities(): Observable<string[]> {
    return this.selectedAbilities$.asObservable();
  }

  getCurrentSelectedAbilities(): string[] {
    return this.selectedAbilities$.getValue();
  }

  setSelectedAbilities(abilities: string[]): void {
    this.selectedAbilities$.next(abilities);
  }

  clearSelectedAbilities(): void {
    this.selectedAbilities$.next([]);
  }

  getAvailableAbilities(): Observable<string[]> {
    return this.availableAbilities$.asObservable();
  }

  setAvailableAbilities(abilities: string[]): void {
    this.availableAbilities$.next(abilities);
  }

  batchUpdateInstallDates(
    projectId: string,
    updates: BatchUpdateInstallDateItem[],
    additions: BatchAddInstallDateItem[],
    removals: BatchRemoveInstallDateItem[],
    branchDate: string,
  ): Observable<BatchUpdateProjectInstallDatesResponse> {
    const request = new BatchUpdateProjectInstallDatesRequest({
      branchDate: parseLocalIsoDate(branchDate)!,
      updates: updates.map((u) => new BatchUpdateItemDto({ id: u.id, date: parseLocalIsoDate(u.date)!, foremanUserId: u.foremanUserId, startTime: u.startTime, endTime: u.endTime, crewMembers: (u.crewMembers ?? []).map(c => new CrewMemberAllocationDto({ id: c.id, userId: c.userId, allocationPercentage: c.allocationPercentage ?? undefined })) })),
      additions: additions.map((a) => new BatchAddItemDto({ date: parseLocalIsoDate(a.date)!, foremanUserId: a.foremanUserId, startTime: a.startTime, endTime: a.endTime, crewMembers: (a.crewMembers ?? []).map(c => new CrewMemberAllocationDto({ userId: c.userId, allocationPercentage: c.allocationPercentage ?? undefined })) })),
      removals: removals.map((r) => new BatchRemoveItemDto({ id: r.id })),
    });
    return this.sdk.batchUpdateProjectInstallDates(projectId, request);
  }

  batchUpdateCustomerSatisfactionVisits(
    projectId: string,
    updates: BatchUpdateInstallDateItem[],
    additions: BatchAddInstallDateItem[],
    removals: BatchRemoveInstallDateItem[],
    branchDate: string,
  ): Observable<BatchUpdateCustomerSatisfactionVisitsResponse> {
    const request = new BatchUpdateCustomerSatisfactionVisitsRequest({
      branchDate: parseLocalIsoDate(branchDate)!,
      updates: updates.map((u) => new BatchUpdateItemDto({ id: u.id, date: parseLocalIsoDate(u.date)!, foremanUserId: u.foremanUserId, startTime: u.startTime, endTime: u.endTime, crewMembers: (u.crewMembers ?? []).map(c => new CrewMemberAllocationDto({ id: c.id, userId: c.userId, allocationPercentage: c.allocationPercentage ?? undefined })) })),
      additions: additions.map((a) => new BatchAddItemDto({ date: parseLocalIsoDate(a.date)!, foremanUserId: a.foremanUserId, startTime: a.startTime, endTime: a.endTime, crewMembers: (a.crewMembers ?? []).map(c => new CrewMemberAllocationDto({ userId: c.userId, allocationPercentage: c.allocationPercentage ?? undefined })) })),
      removals: removals.map((r) => new BatchRemoveItemDto({ id: r.id })),
    });
    return this.sdk.batchUpdateCustomerSatisfactionVisits(projectId, request);
  }

  getTravelAssignments(foremanUserId: string): Observable<TravelAssignmentResponse[]> {
    return this.sdk.getForemanTravelAssignments(foremanUserId);
  }

  createTravelAssignment(
    foremanUserId: string,
    body: CreateTravelAssignmentBody,
  ): Observable<TravelAssignmentResponse> {
    return this.sdk.createForemanTravelAssignment(
      foremanUserId,
      new CreateTravelAssignmentRequest({
        startDate: parseLocalIsoDate(body.startDate)!,
        endDate: parseLocalIsoDate(body.endDate)!,
        destinationBranchId: body.destinationBranchId,
        crewMemberUserIds: body.crewMemberUserIds,
      }),
    );
  }

  updateTravelAssignmentCrew(
    travelAssignmentId: string,
    body: UpdateTravelAssignmentCrewRequest,
  ): Observable<void> {
    return this.sdk.updateForemanTravelAssignmentCrew(travelAssignmentId, body);
  }

  deleteTravelAssignment(travelAssignmentId: string): Observable<void> {
    return this.sdk.deleteForemanTravelAssignment(travelAssignmentId);
  }

  getEligibleCrew(
    foremanUserId: string,
    startDate: Date,
    endDate: Date,
  ): Observable<CrewMemberResponse[]> {
    const start = formatLocalIsoDate(startDate);
    const end = formatLocalIsoDate(endDate);
    return this.http.get<CrewMemberResponse[]>(
      `${this.baseUrl}/api/v1/foreman-travel-assignments/${encodeURIComponent(foremanUserId)}/eligible-crew?startDate=${start}&endDate=${end}`,
    );
  }

  savePreferredCrew(foremanUserId: string, crewMemberUserIds: string[]): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/api/v1/foreman-configuration/${encodeURIComponent(foremanUserId)}/preferred-crew`,
      { crewMemberUserIds },
    );
  }

  getForemanDetail(foremanUserId: string): Observable<ForemanDetailInfo> {
    return this.http.get<ForemanDetailInfo>(
      `${this.baseUrl}/api/v1/foreman-configuration/${encodeURIComponent(foremanUserId)}`,
    );
  }

  getPreferredCrewOptions(foremanUserId: string): Observable<CrewMemberResponse[]> {
    return this.http.get<CrewMemberResponse[]>(
      `${this.baseUrl}/api/v1/foreman-configuration/${encodeURIComponent(foremanUserId)}/available-crew`,
    );
  }

  getAssignedPreferredCrew(foremanUserId: string): Observable<CrewMemberResponse[]> {
    return this.http.get<CrewMemberResponse[]>(
      `${this.baseUrl}/api/v1/foreman-configuration/${encodeURIComponent(foremanUserId)}/preferred-crew`,
    );
  }

  getCrewBonusPercentages(foremanUserId: string): Observable<CrewBonusPercentageResponse[]> {
    return this.http.get<CrewBonusPercentageResponse[]>(
      `${this.baseUrl}/api/v1/foreman-configuration/${encodeURIComponent(foremanUserId)}/crew-bonus-percentages`,
    );
  }

  saveCrewBonusPercentages(
    foremanUserId: string,
    rows: { userId: string; bonusPercent: number | null }[],
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/api/v1/foreman-configuration/${encodeURIComponent(foremanUserId)}/crew-bonus-percentages`,
      { rows },
    );
  }

  getDefaultForemanAvailability(
    foremanUserId: string,
  ): Observable<GetDefaultForemanAvailabilityResponse> {
    return this.http.get<GetDefaultForemanAvailabilityResponse>(
      `${this.baseUrl}/api/v1/default-foreman-availability/${encodeURIComponent(foremanUserId)}`,
    );
  }

  getForemanAvailabilityDates(
    foremanUserId: string,
    branchId: string,
  ): Observable<GetForemanAvailabilityDatesResponse> {
    return this.sdk.getForemanAvailabilityDates(foremanUserId, branchId);
  }

  createForemanAvailability(
    foremanUserId: string,
    date: Date,
    branchId: string,
  ): Observable<CreateForemanAvailabilityResult> {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return this.http.post<CreateForemanAvailabilityResult>(
      `${this.baseUrl}/api/v1/foreman-availability`,
      { foremanUserId, date: `${year}-${month}-${day}`, branchId },
    );
  }

  getDeletionReasons(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/api/v1/foreman-availability/deletion-reasons`);
  }

  deleteForemanAvailability(
    foremanUserId: string,
    date: Date,
    branchId: string,
    deletionReason: string,
  ): Observable<DeleteForemanAvailabilityResult> {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return this.http.delete<DeleteForemanAvailabilityResult>(
      `${this.baseUrl}/api/v1/foreman-availability`,
      { body: { foremanUserId, date: `${year}-${month}-${day}`, branchId, deletionReason } },
    );
  }

  saveDefaultForemanAvailabilityWithPropagation(
    foremanUserId: string,
    branchId: string,
    payload: SaveForemanConfigurationPayload,
  ): Observable<SaveDefaultAvailabilityResult> {
    return this.http.put<SaveDefaultAvailabilityResult>(
      `${this.baseUrl}/api/v1/default-foreman-availability/${encodeURIComponent(foremanUserId)}/propagate?branchId=${encodeURIComponent(branchId)}`,
      payload,
    );
  }
}

export interface DefaultAvailabilityDayInfo {
  dayOfWeek: number;
  isAvailable: boolean;
}

export interface SaveDefaultAvailabilityEntry {
  dayOfWeek: number;
}

export interface GetDefaultForemanAvailabilityResponse {
  projectTypeIds: string[];
  days: DefaultAvailabilityDayInfo[];
}

export interface SaveForemanConfigurationPayload {
  projectTypeIds: string[];
  days: SaveDefaultAvailabilityEntry[];
}

export interface DeleteForemanAvailabilityResult {
  deleted: boolean;
}

export interface CreateForemanAvailabilityResult {
  created: boolean;
}

export interface SaveDefaultAvailabilityResult {
  defaultsCreated: number;
  defaultsUpdated: number;
  defaultsRemoved: number;
  availabilitiesCreated: number;
  availabilitiesDeleted: number;
  availabilitiesUpdated: number;
}

export interface CreateTravelAssignmentBody {
  startDate: string;
  endDate: string;
  destinationBranchId: string;
  crewMemberUserIds?: string[];
}

